import { BINARY_OPERATORS, OPERATORS, OPERATOR_PRIORITY } from '../constant';
import { Expression, IEventEmitter, Leaves, NestedReferenceInfo, Operand, Operator, StateMap } from '../interface';

class FSM<T extends IEventEmitter = never> {
  private readonly reference: T;
  private readonly triggered: Record<string, boolean>;
  private readonly states: StateMap<T>;
  public state: string;

  constructor(reference: T, states: StateMap<T>, state: string) {
    this.triggered = {};
    this.reference = reference;
    this.states = states;
    this.state = state;
    this.reference.emit('transition', this.state, state);
  }

  public update(): void {
    for (const state in this.states[this.state]) {
      if (this.evaluate(this.states[this.state][state])) {
        this.transition(state);
        break;
      }
    }

    this.clear();
  }

  public trigger(...events: Array<string>): void {
    for (let eventName of events) {
      this.triggered[eventName] = true;
    }
  }

  public transition(state: string): void {
    this.reference.emit('transition', this.state, state);
    this.state = state;
  }

  public clear(): void {
    for (let eventName in this.triggered) {
      this.triggered[eventName] = false;
    }
  }

  public evaluate(expression: Expression<T>): boolean {
    let operands: Array<Operand<T>> = [];
    let operators: Array<Operator> = [];
    let operandA: Operand<T>;
    let operator: Operator;
    let operandB: Operand<T>;

    for (let i = 0; i < expression.length; i++) {
      if (Array.isArray(expression[i])) {
        // Expression
        operands.push(this.evaluate(expression[i] as Expression<T>));
      } else if (typeof expression[i] === 'object') {
        // Reference
        if ((expression[i] as NestedReferenceInfo<T>).refs) {
          operands.push(this.peakReference((expression[i] as NestedReferenceInfo<T>).refs));
        } else if ((expression[i] as NestedReferenceInfo<T>).events) {
          operands.push(this.peakEvents((expression[i] as NestedReferenceInfo<T>).events));
        }
      } else if (OPERATORS[expression[i] as keyof typeof OPERATORS]) {
        // Operator
        while (operators.length && OPERATOR_PRIORITY[expression[i] as Operator][operators[operators.length - 1]]) {
          operator = operators.pop() as Operator;
          operandB = BINARY_OPERATORS[operator as keyof typeof BINARY_OPERATORS]
            ? (operands.pop() as Operand<T>)
            : false;
          operandA = operands.pop() as Operand<T>;
          operands.push(this.calculate(operandA, operator, operandB));
        }
        operators.push(expression[i] as Operator);
      } else {
        // Operand
        operands.push(expression[i] as Operand<T>);
      }
    }

    while (operators.length) {
      operator = operators.pop() as Operator;
      operandB = BINARY_OPERATORS[operator as keyof typeof BINARY_OPERATORS] ? (operands.pop() as Operand<T>) : false;
      operandA = operands.pop() as Operand<T>;
      operands.push(this.calculate(operandA, operator, operandB));
    }

    if (operands.length === 1) {
      return operands[0] as boolean;
    } else {
      throw new Error('Invalid expression');
    }
  }

  private peakReference(keys?: Leaves<T>): number | string | boolean {
    let reference: T = this.reference;

    for (const key of keys || []) {
      reference = (reference as any)[key];
    }

    return reference as unknown as number | string | boolean;
  }

  private peakEvents(events?: Array<string>): boolean {
    let result: boolean = true;

    for (let eventName of events || []) {
      result = result && this.triggered[eventName];

      if (!result) {
        break;
      }
    }

    return result;
  }

  private calculate(operandA: Operand<T>, operator: Operator, operandB: Operand<T>): Operand<T> {
    switch (operator) {
      case '&&':
        return operandA && operandB;
      case '||':
        return operandA || operandB;
      case '>=':
        return operandA >= operandB;
      case '<=':
        return operandA <= operandB;
      case '>':
        return operandA > operandB;
      case '<':
        return operandA < operandB;
      case '===':
        return operandA === operandB;
      case '!==':
        return operandA !== operandB;
      case '+':
        return (operandA as number) + (operandB as number);
      case '-':
        return (operandA as number) - (operandB as number);
      case '*':
        return (operandA as number) * (operandB as number);
      case '/':
        return (operandA as number) / (operandB as number);
      case '%':
        return (operandA as number) % (operandB as number);
      case '**':
        return (operandA as number) ** (operandB as number);
      case '<<':
        return (operandA as number) << (operandB as number);
      case '>>':
        return (operandA as number) >> (operandB as number);
      case '>>>':
        return (operandA as number) >>> (operandB as number);
      case '&':
        return (operandA as number) & (operandB as number);
      case '|':
        return (operandA as number) | (operandB as number);
      case '^':
        return (operandA as number) ^ (operandB as number);
      case '!':
        return !operandA;
      case 'abs':
        return Math.abs(operandA as number);
      case 'sqrt':
        return Math.sqrt(operandA as number);
      case 'floor':
        return Math.floor(operandA as number);
      case 'ceil':
        return Math.ceil(operandA as number);
      case 'round':
        return Math.round(operandA as number);
      default:
        throw new Error(`Unknown operator: ${operator}`);
    }
  }
}

export default FSM;
